import { OwnerLoadGate } from '@src/components/project-setup/OwnerLoadGate';
import { AgentHomeAfterLoad } from '@src/project-home-page/AgentHomeAfterLoad';
import {
  PROVIDER_PROJECT_PARAM,
  HOME_AGENT_PARAM,
  CONSUMER_PROJECT_PARAM,
} from '@src/project-home-page/dependency-home';
import { PrimaryContentRegion, PrimaryContentFallback } from '@sdk/react/primary-content';
import { SpecEditor } from '@src/components/spec-editor/SpecEditor';
import { useAgentContext } from '@src/components/agent-layout/agent-layout';
import { AIConfigView } from '@src/components/ai-config-view';
import { ArtifactsView } from '@src/components/artifacts';
import { AssistanceViewer } from '@src/components/assistance-viewer/AssistanceViewer';
import CodeEditor from '@src/components/code-editor/CodeEditor';
import { AssetCompareView } from '@src/components/code-editor/AssetCompareView';
import DiffViewer from '@src/components/code-editor/DiffViewer';
import { DocsViewer } from '@src/components/docs-viewer/DocsViewer';
import { ExplorerView } from '@src/components/explorer-view';
import { HooksManager } from '@src/components/hooks-manager';
import { LensViewer } from '@src/components/lens-viewer';
import { MachineOverview } from '@src/components/machine-overview/machine-overview';
import { MarkdownViewer } from '@src/components/markdown-viewer';
import { SettingsView } from '@src/components/settings-view/SettingsView';
import { PreferencesView } from '@src/components/preferences-view/PreferencesView';
import { DesktopPage } from '@src/pages/desktop/DesktopPage';
import { TasksRedirect } from '@src/components/tasks-viewer/TasksRedirect';
import { HomeLanding } from '@src/pages/home-landing';
import { HubHome } from '@src/pages/hub-home/HubHome';
import { HubRecordsView } from '@src/pages/hub-browse/HubRecordsView';
import { HubEntityView } from '@src/pages/hub-browse/HubEntityView';
import { HubProjectPage } from '@src/pages/hub-project/HubProjectPage';
import { LiveStatus } from '@src/pages/live-status';
import { SearchView } from '@src/pages/search-view/SearchView';

import { PageId } from '@sdk';
import { useAuth } from '@sdk/react/hooks';
import { AssetsPage } from '@src/components/assets/AssetsPage';
import { HubAssetsPage } from '@src/components/assets/HubAssetsPage';
import { CollaborationPage, LiveSessionView } from '@src/components/collaboration';
import { CredentialsView } from '@src/components/credentials-view/CredentialsView';
import { CapabilitiesView } from '@src/components/capabilities-view';
import { ConversationRoute } from '@src/components/conversation';
import { StreamInboxView } from '@src/components/stream-inbox-view/StreamInboxView';
import { AgentStreamInboxView } from '@src/components/stream-inbox-view/AgentStreamInboxView';
import { TabbedTerminal } from '@src/components/terminal';
import { HubTerminal } from '@src/components/terminal/HubTerminal';
import { WebappViewer } from '@src/components/webapp-viewer';
import { useActiveViewer } from '@src/hooks/flow-hooks';
import { useViewerStore } from '@src/hooks/flow-hooks/useViewerStore';
import { Tab, tabForDockKey } from '@sdk';
import { useTerminalTabs, useTabLifecycle } from '@src/tabs/use-tab-manager';
import { DockPointer } from '@src/navigation/DockPointer';
import { appDockAddress } from '@src/navigation/app-dock';
import { AppSlot } from '@src/components/app-pool/AppSlot';
import { appViewKey } from '@src/components/app-pool/app-view-key';
import { NavigatorSlot } from '@src/navigation/NavigatorSlot';
import {
  DEFAULT_NAVIGATOR_POLICY,
  NavigatorCollapsePolicyContext,
  POPOUT_NAVIGATOR_POLICY,
} from '@src/components/navigator-panel/NavigatorPanel';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { SpecRoute } from '@src/pages/spec/SpecRoute';
import { GraphContextViewer } from '@src/components/graph-context/GraphContextViewer';
import { DiagnosisViewer } from '@src/components/diagnosis-viewer/DiagnosisViewer';
import { useSurveyStore } from '@src/store/use-survey-store';
import { TabLifecycleState } from '@sdk';
import { DockLoadErrorView } from '@src/components/agent-layout/DockLoadErrorView';
import { useDockLoadError } from '@src/routes/loaders/dock-load-error-store';
import { ViewType, VIEWER_REGISTRY } from '@src/types/ViewType';
import { ContentEpochContext } from '@src/contexts/content-epoch-context';
import { OrganizationPage } from '@src/components/organization/organization-page';
import { useIsVibe } from '@src/components/view-mode';
import { AlertTriangle } from 'lucide-react';
import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo } from 'react';
import { lazyWebglView } from '@src/components/graph-view/webglSupport';

// Lazy-loaded: GraphView pulls in sigma.js + @sigma/node-image, which run
// WebGL init (gl.getParameter) at module load. Importing it eagerly crashes
// the entire app in any WebGL-less context (headless browsers, GPU-disabled
// CI, software-render fallbacks). Loading it only when the graph tab opens
// keeps app bootstrap independent of WebGL availability, and the
// Every sigma-bearing view goes through `lazyWebglView`, which keeps the sigma
// chunk from ever being evaluated when WebGL is missing — the tab shows
// WebglUnavailableView instead of crashing. Use it for any new graph view:
// repeating the check by hand is what let TagGraphView and GenericSubgraphView
// ship unguarded.
const GraphView = lazyWebglView(() =>
  import('@src/components/graph-view/GraphView').then((m) => ({ default: m.GraphView })),
);
// Lazy like its neighbours: the portal drags react-markdown + the article
// renderer, which no user who never opens a help desk should pay for.
const HelpdeskPortalPage = lazy(() =>
  import('@src/components/helpdesk/HelpdeskPortalPage').then((m) => ({ default: m.HelpdeskPortalPage })),
);
// Lazy like its neighbours: both hub token screens pull recharts, which no
// user who never opens them should pay for in this chunk.
const LlmSourcesView = lazy(() =>
  import('@src/components/llm-sources/LlmSourcesView').then((m) => ({ default: m.LlmSourcesView })),
);
const LlmEndpointsView = lazy(() =>
  import('@src/components/llm-endpoints/LlmEndpointsView').then((m) => ({ default: m.LlmEndpointsView })),
);
// Lazy like its neighbours: a one-question onboarding screen most sessions never open.
const LlmSetupView = lazy(() =>
  import('@src/components/llm-setup/LlmSetupView').then((m) => ({ default: m.LlmSetupView })),
);
// Lazy like its neighbours: one screen (hub page and desk alike) most sessions never open.
const InboundView = lazy(() => import('@src/components/inbound/InboundView').then((m) => ({ default: m.InboundView })));
const TokenPlanView = lazy(() =>
  import('@src/components/token-plan/TokenPlanView').then((m) => ({ default: m.TokenPlanView })),
);
const WorldView = lazyWebglView(() =>
  import('@src/components/graph-view/GraphView').then((m) => ({ default: m.WorldView })),
);
const TagGraphView = lazyWebglView(() =>
  import('@src/components/graph-view/TagGraphView').then((m) => ({ default: m.TagGraphView })),
);
const GenericSubgraphView = lazyWebglView(() =>
  import('@src/components/graph-view/SubgraphView').then((m) => ({ default: m.GenericSubgraphView })),
);
// Lazy like GRAPH — keeps @xyflow/react out of app bootstrap.
const GraphWorkflowsView = lazy(() =>
  import('@src/components/graph-workflows/GraphWorkflowsView').then((m) => ({ default: m.GraphWorkflowsView })),
);
const AutomationsView = lazy(() =>
  import('@src/components/automations/AutomationsView').then((m) => ({ default: m.AutomationsView })),
);
const AssetListView = lazy(() =>
  import('@src/components/asset-counters/AssetListView').then((m) => ({ default: m.AssetListView })),
);
const DataSourcesView = lazy(() =>
  import('@src/components/data-sources/DataSourcesView').then((m) => ({ default: m.DataSourcesView })),
);
const SchemaTreeView = lazy(() =>
  import('@src/components/schema-tree/SchemaTreeView').then((m) => ({ default: m.SchemaTreeView })),
);
const RagView = lazy(() => import('@src/components/rag/RagView').then((m) => ({ default: m.RagView })));
const RunsView = lazy(() => import('@src/components/runs/RunsView').then((m) => ({ default: m.RunsView })));
const SurveyView = lazy(() => import('@src/components/survey/SurveyView').then((m) => ({ default: m.SurveyView })));
const ShowView = lazy(() => import('@src/components/show-view/ShowView').then((m) => ({ default: m.ShowView })));
const AskView = lazy(() => import('@src/components/ask/AskView'));
const AssistantPopoutView = lazy(() => import('@src/components/floating-chat/AssistantPopoutView'));
const AppHost = lazy(() => import('@src/components/app-host/AppHost').then((m) => ({ default: m.AppHost })));
const DocsGraphView = lazy(() =>
  import('@src/components/graph-view/DocsGraphView').then((m) => ({ default: m.DocsGraphView })),
);
import { UserDropdown } from './user-dropdown/user-dropdown';
import { UnifiedTabStrip } from './unified-tab-strip';
import { Trans } from '@lingui/react/macro';

// The curated set of "creator" surfaces that get the chrome-less Lovable-style
// Vibe treatment (chat + live preview + code/docs). Anything not here keeps the
// normal Standard chrome even in Vibe so its tabs/navigator remain usable.
const VIBE_CREATOR_SURFACES: ReadonlySet<ViewType> = new Set([
  ViewType.HOME,
  ViewType.CONVERSATION,
  ViewType.SHELL,
  ViewType.VIBE,
  ViewType.AGENTIC_PROCESS,
  ViewType.WEB_APP,
  ViewType.APP,
  ViewType.EDITOR,
  ViewType.DIFF,
  ViewType.MARKDOWN,
  ViewType.DOCS,
  ViewType.PLAN,
  ViewType.SPEC,
]);

/** ``minimalChrome`` forces the chrome-less arrangement (no tab strip / navigator
 *  / border framing) regardless of view mode — used when ContentPanel is embedded
 *  inside a host layout that owns its own chrome (the vibe workspace mounts it as
 *  the display for a child tab). Generalizes the vibe-creator-surface suppression
 *  to any embedded host (future: the win/ layout). */
/**
 * True under a host that draws the global tab strip ITSELF (`flow-page`), above
 * every layout: one owner, one mounted strip, so switching between a Vibe tab and
 * another tab never remounts it. A ContentPanel under it frames its body as if the
 * strip were its own, but does not draw a second one.
 */
export const TabStripOwnedAbove = createContext(false);

export function ContentPanel(props: { minimalChrome?: boolean; contentEpoch?: number } = {}) {
  return (
    <PrimaryContentRegion>
      <ContentPanelBody {...props} />
    </PrimaryContentRegion>
  );
}

function ContentPanelBody({
  minimalChrome = false,
  contentEpoch,
}: { minimalChrome?: boolean; contentEpoch?: number } = {}) {
  // Get navigation instance for URL-first architecture
  const { navigation, currentDock, isDockUrl, windowMode } = useDockNavigation();
  const activeLifecycle = useTabLifecycle(currentDock?.tabHash);
  const dockLoadError = useDockLoadError(currentDock);

  const { user } = useAuth();

  const { agent, project } = useAgentContext();

  // Sync flow focus and URL dock state to viewer store
  useActiveViewer();

  const terminalTabs = useTerminalTabs();

  /** Navigate to a terminal tab by its dockPointer. */
  const navigateToTab = useCallback(
    (tab: Tab) => {
      if (tab.dockPointer) navigation.openDock(tab.dockPointer);
    },
    [navigation],
  );

  // State from viewer store (overview-axis only — the header tab membership
  // moved to the unified TabStrip, tab-management.md Part 3 U1)
  const { currentContext } = useViewerStore();

  // Survey state (shared with chat-panel)
  const { activeSurveyData, onSurveyComplete } = useSurveyStore();

  const handleExplorerFileSelect = useCallback(
    (path: string) => {
      // Extension dispatch (md → assets document viewer, else code editor)
      // lives in openFile — the explorer must not hard-code a viewer.
      navigation.openFile(path);
    },
    [navigation],
  );

  // Shell entity sync is automatic via DataOp stream — no manual sync needed.

  // When the URL's active terminal is closing (is_disabled), redirect to the
  // first alive tab. A pointer-less shell URL is loader-owned (the loader
  // resolves the default target), so we only act when a tab matches the URL.
  useEffect(() => {
    if (!DockPointer.isSessionView(currentDock?.viewType) || !currentDock?.pointer) return;
    const active = tabForDockKey(terminalTabs, currentDock.tabHash);
    if (active?.is_disabled) {
      const alive = terminalTabs.find((t) => t.id !== active.id && !t.is_disabled);
      if (alive) navigateToTab(alive);
    }
  }, [currentDock, navigateToTab, terminalTabs]);

  const { editorActivePath, checkpointHash } = useMemo(() => {
    return {
      editorActivePath: currentContext?.codeRef?.path,
      checkpointHash: currentContext?.viewerOptions?.checkpointHash,
    };
  }, [currentContext]);

  // The body's viewType is the URL's dock viewType; no dock URL → Home (the
  // landing). URL-first: the URL is the single source of "what's shown".
  const bodyViewType = isDockUrl && currentDock?.viewType ? currentDock.viewType : ViewType.HOME;
  const activeOpenFailed = activeLifecycle?.state === TabLifecycleState.OpenFailed;

  // Vibe mode = the simplest creator skin. On the curated creator surfaces (chat,
  // live preview, code/diff, docs), Vibe strips the tab strip + navigator for a
  // Lovable-style chrome-less canvas. Every OTHER surface (assets, graph,
  // triggers, settings…) falls back to the normal Standard chrome so navigation
  // still works. Skin-layer rule: arrangement/visibility only — never data.
  // Chrome-less either because the host asked (embedded, e.g. the vibe display
  // pane) or a Vibe creator surface. No longer vibe-only — hence `suppressChrome`.
  const isVibe = useIsVibe();
  // Only a dock that HAS a mode (a session, or a child its workspace hosts) can
  // be a Vibe creator surface; an ordinary page keeps its navigator and framing.
  const suppressChrome =
    minimalChrome || (isVibe && !!currentDock?.carriesViewMode && VIBE_CREATOR_SURFACES.has(bodyViewType));

  // Chrome-less when the surface is full-bleed (Home — a welcome landing, not a
  // tabbed workspace), in the win/ focus layout, or a Vibe creator surface.
  // `chrome` (the registry "takeover" bit) is separate from
  // `DockPointer.tabHash` (chip-or-not).
  // The tab strip is persistent fixture chrome: it stays mounted on fullbleed
  // surfaces (Home) so open tabs never vanish; only win/ mode and Vibe creator
  // surfaces hide it. `hideChrome` (strip conditions + fullbleed) governs the
  // navigator/border framing — derived from `showTabStrip` so the shared
  // conditions exist exactly once.
  // Every real tab shows the strip — a Vibe tab is a tab like any other. Only the
  // win/ focus layout and an embedded host (`minimalChrome`: the Vibe display pane,
  // the asset workspace, which draw under flow-page's strip) go without. The Vibe
  // creator surfaces still drop the NAVIGATOR and framing (`suppressChrome`).
  const showTabStrip = !windowMode && !minimalChrome;
  const stripOwnedAbove = useContext(TabStripOwnedAbove);
  const hideChrome = !showTabStrip || VIEWER_REGISTRY[bodyViewType]?.chrome === 'fullbleed';

  // The single body switch (one place, was duplicated between the overview slot
  // and a per-viewType TabsContent ladder). Renders the surface for `vt`; only
  // the active body is mounted (matches the old radix Tabs, which did not
  // forceMount). `null`/unknown → the Home landing.
  // Hub page (page=hub) renders its OWN small set of views, not the desk switch.
  // Kept as a separate dispatch (not extra cases in the desk switch) so the two
  // SPA-surfaces stay cleanly independent — see PAGES_DOCKPOINTER_SPEC.
  const renderHubBody = (vt: ViewType | null) => {
    switch (vt) {
      case ViewType.WORLDVIEW:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <WorldView />
          </Suspense>
        );
      case ViewType.ORGANIZATION:
        return <OrganizationPage />;
      case ViewType.HUB_RECORDS:
        return <HubRecordsView type={currentDock?.pointer} />;
      case ViewType.STREAM_INBOX:
        // The desk's own Stream Inbox: it reads the hub's conversations through the same SDK calls.
        return <StreamInboxView />;
      case ViewType.ASSET_LIST:
        // The hub home's summary chips open their table here (hub-counters.tsx).
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <AssetListView />
          </Suspense>
        );
      case ViewType.HUB_ENTITY:
        return <HubEntityView pointer={currentDock?.pointer} />;
      case ViewType.CONVERSATION:
        // Reuse the OSS conversation viewer (pure-graph, hub-safe) under page=hub.
        return <ConversationRoute />;
      case ViewType.SHELL:
        // A terminal on a hub compute node (a connected machine's PTY session).
        return <HubTerminal className="h-full" />;
      case ViewType.ASSETS:
        return <HubAssetsPage />;
      case ViewType.PROJECT:
        return <HubProjectPage />;
      case ViewType.CREDENTIALS:
        return <CredentialsView />;
      case ViewType.LLM_ENDPOINTS:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <LlmEndpointsView pointer={currentDock?.pointer} />
          </Suspense>
        );
      case ViewType.TOKEN_PLAN:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <TokenPlanView pointer={currentDock?.pointer} />
          </Suspense>
        );
      case ViewType.INBOUND:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <InboundView />
          </Suspense>
        );
      case ViewType.MACHINES:
        return <HubHome view="machines" />;
      case ViewType.HOME:
      default:
        return <HubHome />;
    }
  };

  const renderBody = (vt: ViewType | null) => {
    if (dockLoadError) {
      return <DockLoadErrorView error={dockLoadError} />;
    }

    // page=hub → the hub surface. Placed after the load-error guard (which is
    // page-agnostic) but before the desk tab/OpenFailed handling, which doesn't
    // apply to the hub (its views don't materialize `tab` entities).
    if (currentDock?.page === PageId.HUB) {
      return renderHubBody(vt);
    }

    if (activeOpenFailed) {
      return (
        <div
          className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-muted-foreground"
          data-testid="tab-open-failed-placeholder"
        >
          <AlertTriangle className="h-9 w-9 text-destructive" />
          <div>
            <h2 className="text-base font-semibold text-foreground">
              <Trans>Tab failed to open</Trans>
            </h2>
            <p className="mt-1 max-w-md text-sm">
              {activeLifecycle?.error || <Trans>The tab content could not be prepared.</Trans>}
            </p>
          </div>
        </div>
      );
    }

    switch (vt) {
      case ViewType.SHELL:
      case ViewType.VIBE:
        // A slot of the terminal pool: unmounting it (any view change) moves the
        // terminal back to the pool, it never tears the runtime down.
        return <TabbedTerminal className="h-full" />;
      case ViewType.EDITOR:
        return <CodeEditor activePath={editorActivePath} />;
      case ViewType.WEB_APP:
        return <WebappViewer />;
      case ViewType.APP: {
        // An app addressed by its artifact, its webapp definition or its endpoint.
        // Distinct from WEB_APP (a raw external URL): the address is stable
        // identity and the endpoint that serves it is derived, which is what makes
        // a shown app bookmarkable and restorable at all. A SLOT of the app pool:
        // the view lives as long as its tab (I6); the epoch reaches it as a prop
        // (a new one reloads the frame once), never as a remount.
        const app = appDockAddress(currentDock);
        const appTarget = currentDock?.targetTypeId;
        return app && appTarget ? (
          <AppSlot
            viewKey={appViewKey(currentDock?.hostProcessId, appTarget.toString(), app.route)}
            tabHash={currentDock?.tabHash ?? ''}
            viewer={{ ...app, reloadKey: contentEpoch }}
          />
        ) : null;
      }
      case ViewType.DIFF:
        if (currentDock?.pointer?.startsWith('asset-compare/')) {
          return <AssetCompareView pointer={currentDock.pointer} />;
        }
        return checkpointHash ? (
          <DiffViewer checkpoint_hash={checkpointHash} />
        ) : (
          <div className="p-4 text-gray-500">
            <Trans>No checkpoint selected</Trans>
          </div>
        );
      case ViewType.MARKDOWN:
        return <MarkdownViewer />;
      case ViewType.SURVEY:
        return activeSurveyData && onSurveyComplete ? (
          <Suspense fallback={<PrimaryContentFallback />}>
            <SurveyView surveyData={activeSurveyData} onComplete={onSurveyComplete} />
          </Suspense>
        ) : (
          <div className="p-6 text-muted-foreground">
            <Trans>No active survey</Trans>
          </div>
        );
      case ViewType.SYSTEM_PROFILE:
        return <LiveStatus />;
      case ViewType.CREDENTIALS:
        return <CredentialsView />;
      // DESK only: every fact it renders is a box fact, and its box action 404s on the hub.
      case ViewType.LLM_SOURCES:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <LlmSourcesView pointer={currentDock?.pointer} />
          </Suspense>
        );
      // DESK only, same reason. No pointer: the screen is one question — see
      // `llm-setup-pointer.ts`.
      case ViewType.LLM_SETUP:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <LlmSetupView />
          </Suspense>
        );
      // The same screen as the hub page's: every claim is the hub's, "This computer" is one more place.
      case ViewType.INBOUND:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <InboundView />
          </Suspense>
        );
      case ViewType.AI_CONFIG:
        return <AIConfigView />;
      case ViewType.HOOKS:
        return <HooksManager />;
      case ViewType.ARTIFACTS:
        return <ArtifactsView />;
      case ViewType.DOCS:
        return <DocsViewer />;
      case ViewType.PLAN:
        return <SpecEditor />;
      case ViewType.ASSISTANCE:
        return agent?.site_config?.feature_flags?.enable_escalation ? <AssistanceViewer /> : null;
      case ViewType.MACHINE:
        return <MachineOverview />;
      case ViewType.EXPLORER:
        return <ExplorerView onFileSelect={handleExplorerFileSelect} />;
      // The retired events / triggers / signals / cron ids are rewritten to this
      // before render (retired-views.ts).
      case ViewType.AUTOMATIONS:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <AutomationsView />
          </Suspense>
        );
      case ViewType.CAPABILITIES:
        return <CapabilitiesView />;
      case ViewType.SHOW:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <ShowView />
          </Suspense>
        );
      case ViewType.ASK:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <AskView />
          </Suspense>
        );
      case ViewType.ASSISTANT:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <AssistantPopoutView />
          </Suspense>
        );
      case ViewType.APPS:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <AppHost />
          </Suspense>
        );
      case ViewType.GRAPH:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <GraphView />
          </Suspense>
        );
      case ViewType.WORLDVIEW:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <WorldView />
          </Suspense>
        );
      case ViewType.TAG:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <TagGraphView />
          </Suspense>
        );
      case ViewType.SUBGRAPH:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <GenericSubgraphView />
          </Suspense>
        );
      case ViewType.GRAPH_WORKFLOWS:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <GraphWorkflowsView />
          </Suspense>
        );
      case ViewType.ASSET_LIST:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <AssetListView />
          </Suspense>
        );
      case ViewType.DATA_SOURCES:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <DataSourcesView />
          </Suspense>
        );
      case ViewType.SCHEMA_TREE:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <SchemaTreeView />
          </Suspense>
        );
      case ViewType.RAG:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <RagView />
          </Suspense>
        );
      case ViewType.PROCESS_RUNS:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <RunsView />
          </Suspense>
        );
      case ViewType.K_BROWSER:
        return (
          <Suspense fallback={<PrimaryContentFallback />}>
            <DocsGraphView />
          </Suspense>
        );
      case ViewType.LENS:
        return <LensViewer />;
      case ViewType.TASKS:
        // Retired: task opens through the generic asset editor. Redirect any
        // lingering /dock/tasks/<id> deep link to editor/task/typeid/…
        return <TasksRedirect />;
      case ViewType.SETTINGS:
        return <SettingsView />;
      case ViewType.PREFERENCES:
        return <PreferencesView />;
      case ViewType.DESKTOP:
        return <DesktopPage />;
      case ViewType.SEARCH:
        return <SearchView />;
      case ViewType.AGENTIC_PROCESS:
        return currentDock?.pointer ? (
          <TabbedTerminal className="h-full" key={currentDock.pointer} processId={currentDock.pointer} />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Trans>No process ID specified</Trans>
          </div>
        );
      case ViewType.LIVE_SESSION:
        return currentDock?.pointer ? (
          <LiveSessionView key={currentDock.pointer} sessionId={currentDock.pointer} />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Trans>No live session specified</Trans>
          </div>
        );
      case ViewType.ASSETS:
        return <AssetsPage />;
      case ViewType.HELPDESK:
        return <HelpdeskPortalPage />;
      case ViewType.PROJECT: {
        // A project dock scoped to a collaboration_room (…/collaboration_room/<id>)
        // renders the collaboration room; a bare project dock is the assets
        // workspace. roomId comes from the URL, so the room view is URL-first.
        const { roomId } = DockPointer.parseProjectPointer(currentDock?.pointer);
        return roomId ? <CollaborationPage /> : <AssetsPage />;
      }
      case ViewType.STREAM_INBOX:
        return <StreamInboxView />;
      case ViewType.AGENT:
        return <AgentStreamInboxView />;
      case ViewType.CONVERSATION:
        return <ConversationRoute />;
      case ViewType.SPEC:
        return <SpecRoute />;
      case ViewType.GRAPH_CONTEXT:
        return <GraphContextViewer pointer={currentDock?.pointer} />;
      case ViewType.DIAGNOSIS:
        return <DiagnosisViewer pointer={currentDock?.pointer} />;
      case ViewType.HOME:
      default:
        return <HomeLanding />;
    }
  };

  const providerProject = currentDock?.options?.[PROVIDER_PROJECT_PARAM];
  const display = currentDock?.hostProcessId ?? '';
  const isAppBody = bodyViewType === ViewType.APP;
  const homeAgent = currentDock?.options?.[HOME_AGENT_PARAM];
  // An app view is brought up by its own lifecycle record (`AppDisplayViewer`), never by a project gate; a
  // provider's other views (its home, its files) still wait for the provider to load.
  const loadProject = isAppBody && !homeAgent ? undefined : providerProject;
  const consumerProject = currentDock?.options?.[CONSUMER_PROJECT_PARAM] || currentDock?.scopeProjectId;
  const renderedBody =
    homeAgent && consumerProject ? (
      <AgentHomeAfterLoad asset={homeAgent} projectId={consumerProject} />
    ) : (
      renderBody(bodyViewType)
    );
  const body = loadProject ? (
    <OwnerLoadGate urlProject={loadProject} display={display}>
      {renderedBody}
    </OwnerLoadGate>
  ) : (
    renderedBody
  );

  return (
    <div data-testid="content-panel" className="flex h-full w-full flex-col bg-background">
      {/* Simple header - show UserDropdown only for non-logged-in users */}
      {!user && !hideChrome && (
        <div className="flex items-center justify-end border-b bg-muted/30 px-3 py-1.5">
          <UserDropdown />
        </div>
      )}

      {/* Unified tab strip — persistent fixture chrome: visible on every
          surface (including Home/fullbleed, where no chip is active but the
          open tabs + openers stay reachable). Only the win/ focus layout and an
          embedded ContentPanel (`minimalChrome`) are deliberately strip-less. */}
      {showTabStrip && !stripOwnedAbove && <UnifiedTabStrip />}

      {/* Zone B — shared left-menu slot, now nested UNDER the tab strip so the
          active view's navigator (assets tree / workflows / docs / triggers /
          chats) is scoped to the current tab's content row rather than spanning
          the full app height beside the tabs. The `border-t` draws the tab
          body's top edge; the active chip's `-mb-px border-b-transparent`
          opens its bottom over this line, so the menu + body read as one panel
          hanging from the current tab (the folder-tab continuum). */}
      <div
        className={`flex min-h-0 flex-1 overflow-hidden ${showTabStrip && !stripOwnedAbove ? 'border-t border-border' : ''}`}
      >
        {/* A win/ popout starts its navigator collapsed and keeps toggles local. */}
        {!suppressChrome && (
          <NavigatorCollapsePolicyContext.Provider
            value={windowMode ? POPOUT_NAVIGATOR_POLICY : DEFAULT_NAVIGATOR_POLICY}
          >
            <NavigatorSlot />
          </NavigatorCollapsePolicyContext.Provider>
        )}

        <div className="relative min-h-0 flex-1 overflow-hidden">
          {/* Matches the proven per-viewType slot layout (plain h-full, no flex-col)
              so xterm fits on first paint — a flex-col parent broke its initial sizing.
              No entrance animation: a tab switch must be visually instant (a fade
              reads as page navigation, not a tab switch). */}
          {/* `contentEpoch` re-keys the body to force a remount. It exists because
              the vibe display is now an ADDRESS, and two of its refresh signals are
              not addresses: a re-`flow show` of the same target (same URL — a no-op
              navigation, yet the file behind it may have been rebuilt) and the
              agent's turn-end (the CLI stream carries no per-file write items, so
              the turn edge is the only "something changed" signal there is — the
              host gates it, so a turn that only talked or read does not bump it;
              see `display-refresh-gate`).
              Undefined for every other host, which keeps the default a plain
              uncontrolled body. */}
          <div key={contentEpoch} className="absolute inset-0 mt-0 h-full flex-1 overflow-auto">
            <ContentEpochContext.Provider value={contentEpoch}>{body}</ContentEpochContext.Provider>
          </div>
        </div>
      </div>
    </div>
  );
}
